import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Loader2, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useExperience } from '../../context/ExperienceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { Button } from '../ui/Button';
import { WizardHeader } from './wizard/WizardHeader';
import { WizardProgress } from './wizard/WizardProgress';
import { WizardStepLabel } from './wizard/WizardStepLabel';
import { UkiyoWaveDivider } from './wizard/WizardDividers';
import { DestinationStep } from './wizard/steps/DestinationStep';
import { DatesStep } from './wizard/steps/DatesStep';
import { DarumaBubble, type DarumaPose } from '../mascot/DarumaBubble';
import { generateTripSuggestion, QuotaReachedError, type TripSuggestion } from '../../lib/trip-suggestions';
import { checkMilestone } from '../../lib/daruma-milestones';

const TOTAL_STEPS = 3;

// Rotating progress phrases shown below Daruma while the AI generates a trip.
// Each one cycles every ~2.5s so the user sees the process is alive.
const GEN_PHRASE_KEYS = [
  'onboarding.tripWizardGenPhase1',
  'onboarding.tripWizardGenPhase2',
  'onboarding.tripWizardGenPhase3',
  'onboarding.tripWizardGenPhase4',
  'onboarding.tripWizardGenPhase5',
  'onboarding.tripWizardGenPhase6',
] as const;

function addMinutesToTime(hhmm: string, mins: number): string | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return null;
  const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + mins;
  const h = Math.floor((total % (24 * 60)) / 60);
  const mm = total % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function FirstTripWizard() {
  const { t } = useTranslation('pages');
  const { user } = useAuth();
  const { isManga, isUkiyo } = useExperience();
  const { state, updateState, completeOnboarding } = useOnboarding();
  const navigate = useNavigate();

  const [showIntro, setShowIntro] = useState(true);
  const [step, setStep] = useState(0);
  const [destinations, setDestinations] = useState<string[]>(state.destinations);
  const [startDate, setStartDate] = useState(state.startDate);
  const [endDate, setEndDate] = useState(state.endDate);
  const [generating, setGenerating] = useState(false);
  const [generatedTrip, setGeneratedTrip] = useState<TripSuggestion | null>(null);
  const [aiError, setAiError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quotaLocked, setQuotaLocked] = useState<QuotaReachedError | null>(null);
  const [genPhaseIdx, setGenPhaseIdx] = useState(0);
  const [saveProgress, setSaveProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (!generating) return;
    setGenPhaseIdx(0);
    const id = window.setInterval(() => {
      setGenPhaseIdx(i => (i + 1) % GEN_PHRASE_KEYS.length);
    }, 2500);
    return () => window.clearInterval(id);
  }, [generating]);

  const toggleDestination = useCallback((dest: string) => {
    setDestinations(prev => {
      const isMetaChoice = dest === 'Not sure yet' || dest === 'Multiple';
      const wasSelected = prev.includes(dest);
      if (wasSelected) return prev.filter(d => d !== dest);
      if (isMetaChoice) return [dest];
      const withoutMeta = prev.filter(d => d !== 'Not sure yet' && d !== 'Multiple');
      return [...withoutMeta, dest];
    });
  }, []);

  const handleDateSkip = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setStep(2);
  }, []);

  const dayCount = (() => {
    if (!startDate || !endDate) return 0;
    return Math.max(1, Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1);
  })();

  const firstDest = destinations[0] || '';

  const canContinue = (): boolean => {
    switch (step) {
      case 0: return destinations.length > 0;
      case 1: return true;
      case 2: return false;
      default: return false;
    }
  };

  const handleGenerate = async () => {
    if (!user || !startDate || !endDate || destinations.length === 0) return;
    setGenerating(true);
    setAiError(false);
    try {
      const interestList = state.interests.length > 0 ? state.interests.join(', ') : 'general sightseeing';

      const isNotSure = destinations.length === 1 && destinations[0] === 'Not sure yet';
      const isMultiple = destinations.length === 1 && destinations[0] === 'Multiple';

      let destinationClause: string;
      if (isNotSure) {
        destinationClause = `across Japan — pick the best 2-3 destinations for a first-time visitor based on the interests (${interestList}) and the ${dayCount}-day duration. Default to iconic cities like Tokyo + Kyoto unless interests suggest otherwise (e.g. nature → add Hakone/Nikko, food → add Osaka, history → add Nara/Hiroshima).`;
      } else if (isMultiple) {
        destinationClause = `covering 2-3 major Japanese cities — recommend the optimal multi-city route based on the ${dayCount}-day duration and interests (${interestList}). Typical first-trip routes: Tokyo → Kyoto → Osaka (Golden Route), or add Hakone/Nara for variety.`;
      } else {
        destinationClause = `with stops in ${destinations.join(', ')}, focusing on ${interestList}`;
      }

      const prompt = `Plan a ${dayCount}-day first trip to Japan ${destinationClause}. Budget level: mid-range. Pace: balanced. Include hotel suggestions near train stations and food recommendations. Selected destinations: ${destinations.join(', ')}.`;
      const suggestion = await generateTripSuggestion({
        locations: [],
        visitedLocations: [],
        prompt,
        startDate,
        endDate,
        flexibility: null,
      });
      setGeneratedTrip(suggestion);
    } catch (err) {
      console.error('[FirstTripWizard] AI generation failed:', err);
      if (err instanceof QuotaReachedError) {
        setQuotaLocked(err);
        const msg = err.period === 'lifetime'
          ? t('aiTrip.quotaToastLifetime', { defaultValue: 'Free plan allows 1 AI trip. Upgrade to keep exploring.' })
          : t('aiTrip.quotaToastMonth', { defaultValue: 'Monthly AI quota reached ({{used}}/{{limit}}). Resets next month or upgrade now.', used: err.used, limit: err.limit });
        toast.error(msg, { duration: 6000 });
      } else {
        setAiError(true);
      }
      setGeneratedTrip(null);
    } finally {
      setGenerating(false);
    }
  };

  const createTripFromSuggestion = async (): Promise<{ id: string | null; error?: string }> => {
    if (!user) return { id: null, error: 'Not authenticated' };
    const firstDestLocal = destinations[0] || 'Japan';

    // Progress model: 1 trip insert + N day inserts + M item inserts.
    // Reporting is optimistic — we count the step as done right after the
    // await resolves (failed inserts still advance the bar so it never stalls).
    const totalDays = generatedTrip?.days?.length || 0;
    const totalStops = generatedTrip?.days?.reduce(
      (n, d) => n + (d.stops?.length || 0),
      0
    ) || 0;
    const total = 1 + totalDays + totalStops;
    let done = 0;
    const tick = () => {
      done += 1;
      setSaveProgress({ done, total });
    };
    setSaveProgress({ done: 0, total });

    const { data: trip, error: tripErr } = await supabase.from('trips').insert({
      user_id: user.id,
      title: generatedTrip?.title || `${firstDestLocal} trip`,
      start_date: startDate,
      end_date: endDate,
    }).select('id').single();
    tick();

    if (tripErr || !trip) {
      console.error('trips insert failed', tripErr);
      return { id: null, error: tripErr?.message || 'Trip insert failed' };
    }

    if (generatedTrip?.days) {
      for (const day of generatedTrip.days) {
        const { data: dayRow, error: dayErr } = await supabase.from('trip_days').insert({
          trip_id: trip.id,
          date: day.date,
        }).select('id').single();
        tick();

        if (dayErr || !dayRow) {
          console.error('trip_days insert failed', dayErr);
          // Still advance past this day's stops so the bar doesn't stall.
          for (let k = 0; k < (day.stops?.length || 0); k++) tick();
          continue;
        }

        if (day.stops) {
          for (let i = 0; i < day.stops.length; i++) {
            const stop = day.stops[i] as any;
            const rawId = stop.location_id;
            const isRealUuid = rawId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawId);

            const startT = stop.suggested_start_time || null;
            const dur = stop.suggested_duration_min || 60;
            const endT = startT && dur ? addMinutesToTime(startT, dur) : null;

            const customLocation = {
              name: stop.location_name || 'Unknown',
              address: stop.bonus_category
                ? `${stop.bonus_category} — ${stop.location_name}`
                : stop.location_name || 'Japan',
              description: stop.bonus_description || stop.notes || null,
              latitude: stop.bonus_lat || null,
              longitude: stop.bonus_lng || null,
              image_url: null,
              google_place_id: null,
            };

            const payload: Record<string, unknown> = {
              trip_day_id: dayRow.id,
              position: i,
              start_time: startT,
              end_time: endT,
              // CRITICAL: mark times as manual so TripDay's auto-layout
              // (recalculateTimesFromIndex) doesn't overwrite the AI-
              // provided + safety-net-adjusted start/end on first render.
              manual_times: startT != null && endT != null,
              notes: stop.notes || stop.bonus_why_visit || null,
              source: 'regular',
              custom_location: customLocation,
            };

            if (isRealUuid) {
              payload.location_id = rawId;
            }

            const { error: itemErr } = await supabase.from('trip_items').insert(payload);
            tick();
            if (itemErr) {
              console.error('trip_items insert failed:', itemErr.message, 'stop:', stop.location_name);
            }
          }
        }
      }
    }

    return { id: trip.id };
  };

  const handleGo = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const result = await createTripFromSuggestion();
      if (!result.id) {
        toast.error(result.error || t('onboarding.tripWizardCreateFailed'));
        setSaving(false);
        return;
      }
      try { localStorage.removeItem('tg_show_trip_wizard_cta'); } catch {}
      await completeOnboarding();
      checkMilestone('trip', 1);
      navigate(`/trip-planner/${result.id}`);
    } catch (err) {
      console.error('handleGo failed', err);
      toast.error((err as Error).message || t('common:toast.error'));
      setSaving(false);
    }
  };

  const handleStartEmpty = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const firstDestLocal = destinations[0] || 'Japan';
      const { data: trip, error: tripErr } = await supabase.from('trips').insert({
        user_id: user.id,
        title: `${firstDestLocal} trip`,
        start_date: startDate,
        end_date: endDate,
      }).select('id').single();
      if (tripErr || !trip) {
        console.error('startEmpty trips insert failed', tripErr);
        toast.error(tripErr?.message || t('onboarding.tripWizardCreateFailed'));
        setSaving(false);
        return;
      }
      try { localStorage.removeItem('tg_show_trip_wizard_cta'); } catch {}
      await completeOnboarding();
      checkMilestone('trip', 1);
      navigate(`/trip-planner/${trip.id}`);
    } catch (err) {
      console.error('handleStartEmpty failed', err);
      toast.error((err as Error).message || t('common:toast.error'));
      setSaving(false);
    }
  };

  const handleStartExploring = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      try { localStorage.removeItem('tg_show_trip_wizard_cta'); } catch {}
      await completeOnboarding();
      navigate('/dream?tab=library');
    } catch {
      setSaving(false);
    }
  };

  const handleSkipAll = useCallback(async () => {
    if (!user) return;
    await completeOnboarding();
  }, [user, completeOnboarding]);

  if (state.phase !== 'first-trip' || !user) return null;

  if (showIntro) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[9000] flex items-center justify-center p-4">
        <div
          className={`relative bg-white dark:bg-gray-800 shadow-warm-xl max-w-md w-full flex flex-col ${
            !isManga && !isUkiyo ? 'rounded-2xl' : ''
          }`}
          style={isManga ? { borderRadius: 0, border: '3px solid #0a0a0a', boxShadow: '6px 6px 0 rgba(0,0,0,0.22)' }
            : isUkiyo ? { borderRadius: 6, border: '1px solid rgba(27,79,138,0.3)', boxShadow: '4px 6px 0 rgba(10,40,90,0.12)' }
            : undefined}
        >
          <div className="px-6 py-8 text-center">
            <DarumaBubble pose="celebrate" message={t('onboarding.tourComplete')} size="lg" />
            <h2 className="text-xl font-display font-bold text-gray-900 dark:text-white mt-6 mb-2">
              {t('onboarding.readyToPlan')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t('onboarding.readyToPlanDesc')}
            </p>
            <div className="flex flex-col gap-3">
              <Button variant="primary" onClick={() => setShowIntro(false)} leftIcon={<Sparkles size={18} />}>
                {t('onboarding.letsGo')}
              </Button>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.setItem('tg_show_trip_wizard_cta', '1'); } catch {}
                  completeOnboarding();
                }}
                className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {t('onboarding.maybeLater')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cardStyle: React.CSSProperties | undefined = isManga
    ? { borderRadius: 0, border: '3px solid #0a0a0a', boxShadow: '6px 6px 0 rgba(0,0,0,0.22)' }
    : isUkiyo
      ? { borderRadius: 6, border: '1px solid rgba(27,79,138,0.3)', boxShadow: '4px 6px 0 rgba(10,40,90,0.12)' }
      : undefined;

  const footerBtnStyle: React.CSSProperties | undefined = isManga
    ? {
        background: '#0a0a0a', color: '#fff', border: '2px solid #0a0a0a', borderRadius: 0,
        fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: '0.06em',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.20)',
      }
    : isUkiyo
      ? {
          background: '#0D3468', color: '#fff', borderRadius: 4,
          fontFamily: "'Noto Serif JP', serif", letterSpacing: '0.04em',
        }
      : undefined;

  const darumaForStep = (): { pose: DarumaPose; message: string } => {
    switch (step) {
      case 0:
        return { pose: 'explain', message: t('onboarding.tripWizardDestination') };
      case 1:
        return { pose: 'think', message: t('onboarding.tripWizardDates') };
      case 2: {
        if (generating) return { pose: 'think', message: t('onboarding.tripWizardGenerating') };
        if (aiError) return { pose: 'explain', message: t('onboarding.tripWizardAiError') };
        if (generatedTrip) {
          return {
            pose: 'celebrate',
            message: t('onboarding.tripWizardDone', {
              stops: generatedTrip.days.reduce((n, d) => n + (d.stops?.length || 0), 0),
              days: generatedTrip.days.length,
            }),
          };
        }
        if (!startDate || !endDate) return { pose: 'travel', message: t('onboarding.tripWizardNoDates') };
        return { pose: 'think', message: t('onboarding.tripWizardGenerating') };
      }
      default:
        return { pose: 'wave', message: '' };
    }
  };

  const daruma = darumaForStep();

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) setStep(s => s + 1);
  };
  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9000] flex items-center justify-center p-4">
      <div
        className={`relative bg-white dark:bg-gray-800 shadow-warm-xl max-w-2xl w-full flex flex-col max-h-[90vh] ${
          !isManga && !isUkiyo ? 'rounded-2xl' : ''
        }`}
        style={cardStyle}
      >
        <button
          onClick={handleSkipAll}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          aria-label={t('onboarding.skipOnboarding')}
          title={t('onboarding.skipOnboardingTooltip')}
        >
          <X size={20} />
        </button>
        <div className="shrink-0">
          <WizardHeader
            step={step}
            isManga={isManga}
            isUkiyo={isUkiyo}
            mangaTitles={[
              t('onboarding.tripWizardStepDestination').toUpperCase(),
              t('onboarding.tripWizardStepDates').toUpperCase() + '?',
              t('onboarding.tripWizardStepLaunch').toUpperCase() + '!',
            ]}
            ukiyoTitles={[
              t('onboarding.tripWizardStepDestination'),
              t('onboarding.tripWizardStepDates'),
              t('onboarding.tripWizardStepLaunch'),
            ]}
            totalSteps={TOTAL_STEPS}
          />
        </div>
        <div className="shrink-0">
          <WizardProgress step={step} isManga={isManga} isUkiyo={isUkiyo} totalSteps={TOTAL_STEPS} />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">
          <div className="px-6 py-5">
            <UkiyoWaveDivider isUkiyo={isUkiyo} />
            <WizardStepLabel
              step={step}
              isUkiyo={isUkiyo}
              standardLabels={[
                t('onboarding.tripWizardStepDestination'),
                t('onboarding.tripWizardStepDates'),
                t('onboarding.tripWizardStepLaunch'),
              ]}
              ukiyoLabels={[
                t('onboarding.tripWizardStepDestination'),
                t('onboarding.tripWizardStepDates'),
                t('onboarding.tripWizardStepLaunch'),
              ]}
              ukiyoKanji={['行', '時', '発']}
            />
            <div className="mb-4">
              <DarumaBubble pose={daruma.pose} message={daruma.message} size="md" animate={generating} />
            </div>

            {step === 0 && (
              <DestinationStep
                selected={destinations}
                onToggle={toggleDestination}
                isManga={isManga}
                isUkiyo={isUkiyo}
              />
            )}
            {step === 1 && (
              <DatesStep
                startDate={startDate}
                endDate={endDate}
                onStartChange={setStartDate}
                onEndChange={setEndDate}
                onSkip={handleDateSkip}
                isEnthusiast={!state.isCasual}
                firstDest={firstDest}
                isUkiyo={isUkiyo}
              />
            )}
            {step === 2 && (
              <div className="space-y-4">
                {startDate && endDate ? (
                  <>
                    {quotaLocked && !generatedTrip && (
                      <div className="p-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 text-sm space-y-2">
                        <div className="font-semibold text-amber-900 dark:text-amber-100">
                          {quotaLocked.period === 'lifetime'
                            ? t('aiTrip.quotaLockedTitleLifetime', { defaultValue: 'Free AI trip already used' })
                            : t('aiTrip.quotaLockedTitleMonth', { defaultValue: 'Monthly AI quota reached' })}
                        </div>
                        <div className="text-xs text-amber-800 dark:text-amber-200">
                          {quotaLocked.period === 'lifetime'
                            ? t('aiTrip.quotaLockedBodyLifetime', { defaultValue: 'The Free plan includes 1 AI-generated trip. You can still start empty and plan manually, or upgrade for more AI suggestions every month.' })
                            : t('aiTrip.quotaLockedBodyMonth', { used: quotaLocked.used, limit: quotaLocked.limit, defaultValue: 'You\'ve used {{used}}/{{limit}} AI suggestions this month. They reset on the 1st, or you can upgrade now.' })}
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => navigate(quotaLocked.upgrade_url)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700"
                          >
                            {t('aiTrip.quotaUpgrade', { defaultValue: 'Upgrade plan' })}
                          </button>
                          <button
                            type="button"
                            onClick={handleStartEmpty}
                            disabled={saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-400 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60"
                          >
                            {t('onboarding.tripWizardStartEmpty')}
                          </button>
                        </div>
                      </div>
                    )}
                    {!generatedTrip && !generating && !aiError && !quotaLocked && (
                      <button
                        type="button"
                        onClick={handleGenerate}
                        className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold"
                        style={footerBtnStyle || { background: 'var(--color-primary-600, #2563eb)', color: '#fff', borderRadius: 12 }}
                      >
                        <Sparkles size={16} /> {t('onboarding.tripWizardGenerate')}
                      </button>
                    )}
                    {generating && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 min-h-[1.5rem]">
                        <Loader2 size={16} className="animate-spin shrink-0" />
                        <span key={genPhaseIdx} className="animate-fade-in">
                          {t(GEN_PHRASE_KEYS[genPhaseIdx])}
                        </span>
                      </div>
                    )}
                    {generatedTrip && !generating && (
                      <div className="space-y-3">
                        <div className="p-4 bg-success-50 dark:bg-success-900/20 text-sm rounded-lg">
                          <div className="font-semibold text-gray-900 dark:text-white mb-1">{generatedTrip.title}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{generatedTrip.summary}</div>
                        </div>
                        {!saving && (
                          <button
                            type="button"
                            onClick={handleGo}
                            className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold"
                            style={footerBtnStyle || { background: 'var(--color-primary-600, #2563eb)', color: '#fff', borderRadius: 12 }}
                          >
                            🚀 {t('onboarding.tripWizardGo')}
                          </button>
                        )}
                        {saving && saveProgress && (
                          <div className="space-y-2" role="status" aria-live="polite">
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                              <span className="flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                {t('onboarding.tripWizardCreating')}
                              </span>
                              <span className="tabular-nums">
                                {Math.min(
                                  100,
                                  Math.round((saveProgress.done / Math.max(1, saveProgress.total)) * 100)
                                )}%
                              </span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary-600 transition-[width] duration-300 ease-out"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (saveProgress.done / Math.max(1, saveProgress.total)) * 100
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {aiError && !generating && (
                      <button
                        type="button"
                        onClick={handleStartEmpty}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold disabled:opacity-60"
                        style={footerBtnStyle || { background: 'var(--color-primary-600, #2563eb)', color: '#fff', borderRadius: 12 }}
                      >
                        {t('onboarding.tripWizardStartEmpty')}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartExploring}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold disabled:opacity-60"
                    style={footerBtnStyle || { background: 'var(--color-primary-600, #2563eb)', color: '#fff', borderRadius: 12 }}
                  >
                    🔍 {t('onboarding.tripWizardStartExploring')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={handleSkipAll}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
          >
            {t('onboarding.skipOnboarding')}
          </button>

          {step > 0 && (
            isManga || isUkiyo ? (
              <button
                type="button"
                onClick={handleBack}
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <ChevronLeft size={16} className="inline -mt-0.5" /> {t('common:common.back')}
              </button>
            ) : (
              <Button variant="outline" onClick={handleBack} leftIcon={<ChevronLeft size={16} />}>
                {t('common:common.back')}
              </Button>
            )
          )}

          <div className="ml-auto">
            {step < TOTAL_STEPS - 1 && (
              isManga || isUkiyo ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canContinue()}
                  className="px-5 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  style={footerBtnStyle}
                >
                  {t('onboarding.continue')} <ChevronRight size={16} className="inline -mt-0.5 ml-1" />
                </button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={!canContinue()}
                  rightIcon={<ChevronRight size={16} />}
                >
                  {t('onboarding.continue')}
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
